import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Award, ArrowLeft, Video, Shirt, Camera, ExternalLink, Clock, 
  DollarSign, CheckCircle, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

// Certification data mapped to pathways
const CERTIFICATIONS = {
  FILM: [
    {
      id: 'FR_LA',
      name: 'Film Ready LA',
      provider: 'Louisiana Entertainment',
      description: 'Comprehensive training for on-set readiness including safety, etiquette, and basic crew positions.',
      duration: '40 hours',
      cost: '$150',
      level: 'Entry',
      link: 'https://www.louisianaentertainment.gov/workforce'
    },
    {
      id: 'SET_SAFETY',
      name: 'Set Safety Certification',
      provider: 'OSHA/Industry Standard',
      description: 'Required safety training for working on professional film and TV sets.',
      duration: '8 hours',
      cost: '$75',
      level: 'Entry',
      link: '#'
    },
    {
      id: 'IATSE_APP',
      name: 'IATSE Apprenticeship',
      provider: 'IATSE Union',
      description: 'Union apprenticeship program for various technical positions in film and television.',
      duration: '2-4 years',
      cost: 'Sponsored',
      level: 'Advanced',
      link: 'https://www.iatse.net/'
    }
  ],
  FASHION: [
    {
      id: 'FASH_READY',
      name: 'Fashion Ready Certification',
      provider: 'DowUrk Academy',
      description: 'Comprehensive introduction to fashion industry practices, from design to retail.',
      duration: '60 hours',
      cost: '$200',
      level: 'Entry',
      link: '#'
    },
    {
      id: 'COSMO_LIC',
      name: 'Cosmetology License',
      provider: 'Louisiana State Board',
      description: 'State-required license for hair, makeup, and beauty professionals.',
      duration: '1500 hours',
      cost: 'Varies',
      level: 'Professional',
      link: '#'
    },
    {
      id: 'MERCH_CERT',
      name: 'Merchandising Certificate',
      provider: 'Fashion Institute',
      description: 'Learn retail merchandising, buying, and visual display strategies.',
      duration: '80 hours',
      cost: '$350',
      level: 'Intermediate',
      link: '#'
    }
  ],
  CONTENT: [
    {
      id: 'GOOGLE_DM',
      name: 'Google Digital Marketing',
      provider: 'Google',
      description: 'Industry-recognized certification in digital marketing fundamentals.',
      duration: '40 hours',
      cost: 'Free',
      level: 'Entry',
      link: 'https://skillshop.withgoogle.com/'
    },
    {
      id: 'ADOBE_CERT',
      name: 'Adobe Certified',
      provider: 'Adobe',
      description: 'Professional certification in Adobe Creative Suite applications.',
      duration: 'Self-paced',
      cost: '$180 per exam',
      level: 'Intermediate',
      link: 'https://www.adobe.com/creativecloud/certiport.html'
    },
    {
      id: 'CC_BOOT',
      name: 'Content Creator Bootcamp',
      provider: 'DowUrk Academy',
      description: 'Intensive program covering content strategy, production, and monetization.',
      duration: '12 weeks',
      cost: '$500',
      level: 'Intermediate',
      link: '#'
    }
  ]
};

const pathwayIcons = {
  FILM: Video,
  FASHION: Shirt,
  CONTENT: Camera
};

const pathwayColors = {
  FILM: 'border-violet-400 bg-violet-500/10',
  FASHION: 'border-pink-400 bg-pink-500/10',
  CONTENT: 'border-cyan-400 bg-cyan-500/10'
};

const levelColors = {
  Entry: 'bg-emerald-500/20 text-emerald-300',
  Intermediate: 'bg-blue-500/20 text-blue-300',
  Advanced: 'bg-purple-500/20 text-purple-300',
  Professional: 'bg-amber-500/20 text-amber-300'
};

export default function StudioCertifications() {
  const [selectedPathway, setSelectedPathway] = useState('ALL');
  const [pathways, setPathways] = useState([]);

  useEffect(() => {
    // Fetch pathway names
    const fetchPathways = async () => {
      try {
        const res = await fetch(`${API}/api/studio/pathways`);
        if (res.ok) {
          const data = await res.json();
          setPathways(data.pathways || []);
        }
      } catch (err) {
        console.error('Error fetching pathways:', err);
      }
    };
    fetchPathways();
  }, []);

  const getCertifications = () => {
    if (selectedPathway === 'ALL') {
      return Object.entries(CERTIFICATIONS).flatMap(([key, certs]) => 
        certs.map(cert => ({ ...cert, pathway: key }))
      );
    }
    return (CERTIFICATIONS[selectedPathway] || []).map(cert => ({ ...cert, pathway: selectedPathway }));
  };

  const certifications = getCertifications();

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="studio-certifications">
      {/* Header */}
      <div className="border-b border-violet-500/30 py-16">
        <div className="container mx-auto px-4">
          <Link to="/legacystudio" className="inline-flex items-center text-violet-300 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyStudio
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-4 bg-yellow-500/20 rounded-xl">
              <Award className="h-10 w-10 text-yellow-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">Certifications</h1>
              <p className="text-violet-200">Industry-recognized credentials to boost your career</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-3 mb-10">
          <Button
            variant={selectedPathway === 'ALL' ? 'default' : 'outline'}
            onClick={() => setSelectedPathway('ALL')}
            className={selectedPathway === 'ALL' 
              ? 'bg-violet-600 hover:bg-violet-700' 
              : 'border-violet-400/50 text-violet-200 hover:bg-violet-500/20'
            }
          >
            All Certifications
          </Button>
          {['FILM', 'FASHION', 'CONTENT'].map(pathway => {
            const Icon = pathwayIcons[pathway];
            return (
              <Button
                key={pathway}
                variant={selectedPathway === pathway ? 'default' : 'outline'}
                onClick={() => setSelectedPathway(pathway)}
                className={selectedPathway === pathway 
                  ? 'bg-violet-600 hover:bg-violet-700' 
                  : 'border-violet-400/50 text-violet-200 hover:bg-violet-500/20'
                }
              >
                <Icon className="h-4 w-4 mr-2" />
                {pathway.charAt(0) + pathway.slice(1).toLowerCase()}
              </Button>
            );
          })}
        </div>

        {/* Certifications Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, idx) => {
            const Icon = pathwayIcons[cert.pathway];
            
            return (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className={`h-full bg-white/5 backdrop-blur border ${pathwayColors[cert.pathway]} hover:border-opacity-100 transition-all`}>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={levelColors[cert.level]}>{cert.level}</Badge>
                      {selectedPathway === 'ALL' && (
                        <Icon className="h-5 w-5 text-violet-400" />
                      )}
                    </div>
                    <CardTitle className="text-white text-lg">{cert.name}</CardTitle>
                    <CardDescription className="text-violet-300">{cert.provider}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-violet-100 text-sm mb-4">{cert.description}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-violet-300 mb-4">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {cert.duration}
                      </div>
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-1" />
                        {cert.cost}
                      </div>
                    </div>

                    {cert.link !== '#' ? (
                      <a href={cert.link} target="_blank" rel="noopener noreferrer">
                        <Button className="w-full bg-violet-600 hover:bg-violet-700">
                          Learn More <ExternalLink className="h-4 w-4 ml-2" />
                        </Button>
                      </a>
                    ) : (
                      <Button className="w-full bg-violet-600/50" disabled>
                        Coming Soon
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16">
          <Card className="bg-gradient-to-br from-yellow-600/20 to-amber-600/20 border-yellow-400/30">
            <CardContent className="py-10 text-center">
              <Award className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-3">Need Guidance?</h2>
              <p className="text-violet-200 mb-6 max-w-lg mx-auto">
                Not sure which certifications are right for your career path? 
                Our AI Creative Coach can help you plan your certification journey.
              </p>
              <Link to="/legacystudio/coach">
                <Button size="lg" className="bg-yellow-500 text-stone-900 hover:bg-yellow-400">
                  Talk to Creative Coach <ChevronRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
